import axios from 'axios';
import { env } from '@config/env';
import { useAuthStore } from '../stores/authStore';

/**
 * Exchange a refresh token for a fresh access/refresh pair.
 *
 * Both login paths ultimately mint **tenant-issued** tokens:
 *   - authMode 'tenant'  → direct `POST /auth/login` on the regional API.
 *   - authMode 'core'    → Core `/portal-auth/login` *proxies* to the same
 *                          Tenant API and returns its tokens.
 * So refresh always targets the regional tenant `POST /auth/refresh`.
 *
 * CRITICAL: this is a raw axios call (it can't go through the `tenantApi`
 * instance because that would recurse through the same 401 interceptor), so it
 * must attach `x-tenant-id` itself. Without it, `tenantMiddleware` falls back to
 * reading the tenant from the API hostname (e.g. `ip4x-za.sudosky.com` →
 * `ip4x-za`), resolves the wrong/no tenant, and the refresh fails — which logs
 * the member out on every access-token expiry.
 */
export async function refreshAuthTokens(
  refreshToken: string,
): Promise<{ accessToken: string; refreshToken?: string }> {
  const auth = useAuthStore.getState();
  const baseURL = auth.regionalApiUrl || env.apiBaseUrl;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (env.appTemplateId) headers['x-app-template-id'] = env.appTemplateId;
  if (auth.tenantId) headers['x-tenant-id'] = auth.tenantId;

  // The tenant API reads `refreshToken` (camelCase); send both casings so the
  // refresh works regardless of which the endpoint expects. A snake_case-only
  // body was silently 400-ing and logging members out on every token expiry.
  const { data } = await axios.post(
    `${baseURL}/auth/refresh`,
    { refreshToken, refresh_token: refreshToken },
    { headers },
  );

  return {
    accessToken: data.accessToken ?? data.access_token,
    refreshToken: data.refreshToken ?? data.refresh_token,
  };
}
